import { useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import type {
  CreateTrainByPackageRequest,
  PackageVersion,
  Train,
} from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import {
  useCreateTrainByPackageMutation,
  useGetAllArtifactsGroupedQuery,
  useGetTrainQuery,
  useListTrainsQuery,
} from '@frontend/api-sync-config';
import { Table, Text, useAlert, useDebouncedValue, useModalControl, useTooltip } from '@frontend/design-system';
import { useAuth } from '../../../providers/auth.provider';
import {
  formatDate,
  getDisplayValue,
  getErrorMessage,
  getPackageVersionKey,
  getTotalArtifacts,
  normalizeArtifactOption,
  normalizeTrainForDisplay,
} from '../shared/helpers';
import { DraftTrainModal } from './draft-train-modal';
import { TrainDetailView } from './train-detail-view';

const TRAINS_PAGE_SIZE = 100;
const ARTIFACTS_PAGE_SIZE = 100;
const INSUFFICIENT_PERMISSIONS_MESSAGE = 'You do not have enough permissions';

interface TrainsPanelProps {
  canManage: boolean;
}

export const TrainsPanel = ({ canManage }: TrainsPanelProps) => {
  const { user } = useAuth();
  const draftSubmissionPendingRef = useRef(false);
  const [selectedTrainId, setSelectedTrainId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const draftModal = useModalControl();
  const alerts = useAlert();
  const debouncedTrainId = useDebouncedValue(searchQuery.trim(), 500);
  const isSearching = debouncedTrainId !== '';
  const isSearchPending = searchQuery.trim() !== debouncedTrainId;

  const {
    data: trainsData,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingTrains,
    isFetchingNextPage,
  } = useListTrainsQuery({
    request: { pageSize: TRAINS_PAGE_SIZE },
    options: {
      onError: (error: Error) => {
        alerts.error(`Failed to load trains: ${getErrorMessage(error)}`);
      },
    },
    httpOptions: { skipValidation: true },
  });

  const {
    data: searchedTrainData,
    isFetching: isFetchingSearchedTrain,
    isError: isSearchedTrainError,
  } = useGetTrainQuery({
    request: { trainId: debouncedTrainId },
    options: {
      enabled: isSearching,
      retry: false,
    },
    httpOptions: { skipValidation: true },
  });

  const {
    data: artifactsData,
    fetchNextPage: fetchNextArtifactsPage,
    hasNextPage: hasNextArtifactsPage,
    isLoading: isLoadingArtifacts,
    isError: isArtifactsError,
    isFetchingNextPage: isFetchingNextArtifactsPage,
  } = useGetAllArtifactsGroupedQuery({
    request: { pageSize: ARTIFACTS_PAGE_SIZE },
    options: {
      enabled: draftModal.modalProps.show,
      onError: (error: Error) => {
        alerts.error(`Failed to load artifacts: ${getErrorMessage(error)}`);
      },
    },
    httpOptions: { skipValidation: true },
  });

  const artifactOptions = useMemo<PackageVersion[]>(() => {
    const optionsByPackageVersion = new Map<string, PackageVersion>();

    for (const page of artifactsData?.pages ?? []) {
      for (const artifact of page.artifacts ?? []) {
        const option = normalizeArtifactOption(artifact);
        if (option?.packageName && option.versionTag) {
          optionsByPackageVersion.set(getPackageVersionKey(option.packageName, option.versionTag), option);
        }
      }
    }

    return Array.from(optionsByPackageVersion.values());
  }, [artifactsData]);

  const totalArtifacts = useMemo(() => getTotalArtifacts(artifactsData?.pages.at(-1)), [artifactsData]);

  const { mutate: createTrainByPackage, isLoading: isCreatingTrain } = useCreateTrainByPackageMutation<Error>({
    httpOptions: { skipValidation: true },
  });

  const handleDraftConfirm = (payload: CreateTrainByPackageRequest) => {
    if (!canManage || draftSubmissionPendingRef.current || isCreatingTrain) return;
    draftSubmissionPendingRef.current = true;

    createTrainByPackage(
      {
        createdBy: user?.username ?? 'unknown',
        packages: payload.packages,
        trainName: payload.trainName,
      },
      {
        onSuccess: () => {
          alerts.success(`Drafted “${payload.trainName}” with ${payload.packages?.length ?? 0} artifact(s).`);
          draftModal.closeModal();
          setCurrentPage(1);
        },
        onError: (error) => {
          alerts.error(`Failed to draft train: ${getErrorMessage(error)}`);
        },
        onSettled: () => {
          draftSubmissionPendingRef.current = false;
        },
      }
    );
  };

  const trains = useMemo(() => {
    if (isSearching) {
      const searchedTrain = searchedTrainData ? normalizeTrainForDisplay(searchedTrainData) : undefined;
      return searchedTrain ? [searchedTrain] : [];
    }

    const currentPageTrains: Train[] = [];

    for (const train of trainsData?.pages[currentPage - 1]?.trains ?? []) {
      const normalizedTrain = normalizeTrainForDisplay(train);
      if (normalizedTrain) currentPageTrains.push(normalizedTrain);
    }

    return currentPageTrains;
  }, [currentPage, isSearching, searchedTrainData, trainsData]);

  const hasCachedNextPage = Boolean(trainsData?.pages[currentPage]);

  const handlePageChange = async (direction: 'next' | 'prev') => {
    if (direction === 'prev') {
      setCurrentPage((page) => Math.max(1, page - 1));
      return;
    }

    const nextPage = currentPage + 1;
    if (hasCachedNextPage) {
      setCurrentPage(nextPage);
      return;
    }

    if (!hasNextPage || isFetchingNextPage) return;

    const result = await fetchNextPage();
    if (result.data?.pages[nextPage - 1]) setCurrentPage(nextPage);
  };

  const selectedTrain = useMemo(
    () => trains.find((t) => t.trainId === selectedTrainId) ?? null,
    [trains, selectedTrainId]
  );

  if (selectedTrain) {
    return <TrainDetailView train={selectedTrain} onBack={() => setSelectedTrainId(null)} />;
  }

  return (
    <div css={wrapperStyle}>
      <Table
        data={trains}
        isLoading={isLoadingTrains || isSearchPending || (isSearching && isFetchingSearchedTrain)}
        isPaginated={!isSearching}
        manualPaginationConfig={
          isSearching
            ? undefined
            : {
                page: currentPage,
                rowsPerPageOptions: [TRAINS_PAGE_SIZE],
                showNumRowSelector: false,
                showGoToPageField: false,
                handleChange: handlePageChange,
                hasNext: hasCachedNextPage || Boolean(hasNextPage),
                hasPrevious: currentPage > 1,
                disablePagination: isFetchingNextPage,
              }
        }
        emptyStateConfig={
          isSearching
            ? {
                type: 'search',
                header: isSearchedTrainError ? 'Unable to load train' : 'Train not found',
                description: `No train was found for ID "${debouncedTrainId}".`,
              }
            : undefined
        }
        hasGlobalSearch
        globalSearchConfig={{
          searchHandler: setSearchQuery,
          placeholder: 'Search by train ID',
        }}
        customToolbarRender={() => (
          <Text weight='bold' size='large' css={toolbarTitleStyle}>
            Release Trains
          </Text>
        )}
        tableActions={[
          {
            type: 'button',
            variant: 'primary',
            label: <DraftTrainActionLabel showPermissionMessage={!canManage} />,
            disabled: !canManage,
            onClick: () => draftModal.openModal(),
          },
        ]}
        colConfig={[
          {
            Header: 'Train Name',
            id: 'trainName',
            accessor: (train: Train) => train.trainName,
            cellRenderer: (value: string) => <Text weight='bold'>{value}</Text>,
            minWidth: 180,
          },
          {
            Header: 'Train ID',
            id: 'trainId',
            accessor: (train: Train) => train.trainId,
            cellRenderer: (value: string) => <Text size='small'>{value}</Text>,
            minWidth: 250,
          },
          {
            Header: 'Promoted Channel',
            id: 'promotedChannel',
            accessor: (train: Train) => train.promotedChannel,
            cellRenderer: (value?: string) =>
              value?.trim() ? <span css={channelChipStyle}>{value.toUpperCase()}</span> : <Text>N/A</Text>,
            maxWidth: 180,
          },
          {
            Header: 'Channel Promoted By',
            id: 'channelPromotedBy',
            accessor: (train: Train) => train.channelPromotedBy,
            cellRenderer: (value?: string) => <Text size='small'>{getDisplayValue(value)}</Text>,
            minWidth: 180,
          },
          {
            Header: 'Created By',
            id: 'createdBy',
            accessor: (train: Train) => train.createdBy,
            cellRenderer: (value?: string) => <Text size='small'>{getDisplayValue(value)}</Text>,
            minWidth: 180,
          },
          {
            Header: 'Created At',
            id: 'createdAt',
            accessor: (train: Train) => train.createdAt,
            cellRenderer: (value?: string) => <Text>{formatDate(value)}</Text>,
            minWidth: 180,
          },
        ]}
        rowActions={{
          onRowClick: (train: Train) => setSelectedTrainId(train.trainId ?? null),
        }}
      />

      <DraftTrainModal
        modalProps={draftModal.modalProps}
        artifacts={artifactOptions}
        totalArtifacts={totalArtifacts}
        isLoadingArtifacts={isLoadingArtifacts}
        isArtifactsError={isArtifactsError && artifactOptions.length === 0}
        hasMoreArtifacts={Boolean(hasNextArtifactsPage)}
        isLoadingMoreArtifacts={isFetchingNextArtifactsPage}
        onLoadMoreArtifacts={fetchNextArtifactsPage}
        isSaving={isCreatingTrain}
        onConfirm={handleDraftConfirm}
      />
    </div>
  );
};

const DraftTrainActionLabel = ({ showPermissionMessage }: { showPermissionMessage: boolean }) => {
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ trigger: 'hover' });

  return (
    <span {...(showPermissionMessage ? triggerProps : {})}>
      + Draft New Train
      {showPermissionMessage && <Tooltip {...tooltipProps}>{INSUFFICIENT_PERMISSIONS_MESSAGE}</Tooltip>}
    </span>
  );
};

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-md);
  padding: var(--fabric-space-gap-lg);
`;

const toolbarTitleStyle = css`
  margin-right: auto;
`;

const channelChipStyle = css`
  display: inline-block;
  padding: var(--fabric-space-padding-xxs) var(--fabric-space-padding-sm);
  border-radius: var(--fabric-radius-full);
  background-color: var(--fabric-color-neutral-5);
  border: var(--fabric-border-width-thin) solid var(--fabric-color-neutral-30);
  color: var(--fabric-color-neutral-70);
  font-size: var(--fabric-font-size-xs);
  font-weight: 600;
  letter-spacing: 0.04em;
`;
